import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  shareReplay,
  Subject,
  throwError,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AccountTypes } from '../models/account_types';

export interface StoreContext {
  business: any;
  parentBusiness: any | null;
  businessName: string;
  parentBusinessName: string | null;
  tableId: string | null;
  kind: 'merchant' | 'delivery' | 'child-merchant';
  latitude: number | null;
  longitude: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  onRefresh = new Subject();

  // The single source of truth for "what business/table are we currently
  // viewing," set once by StoreComponent after it resolves the route params
  // and looks up the business. Any component nested under StoreComponent
  // (header, cart, orders, ...) can subscribe instead of re-deriving the
  // same thing from localStorage.
  private readonly context$ = new BehaviorSubject<StoreContext | null>(null);
  readonly currentContext$: Observable<StoreContext | null> =
    this.context$.asObservable();

  get currentContext(): StoreContext | null {
    return this.context$.value;
  }

  setContext(context: StoreContext): void {
    this.context$.next(context);
  }

  clearContext(): void {
    this.context$.next(null);
  }

  resolveKind(business: any): StoreContext['kind'] {
    if (this.isDeliveryBusiness(business)) {
      return 'delivery';
    }
    return this.isChildBusiness(business) ? 'child-merchant' : 'merchant';
  }

  // Holds only the ONE business currently being viewed (its name, plus its
  // in-flight/resolved business-find request) — not a cache of every
  // business visited this session. Whoever asks first (DeliveryBusinessGuard
  // for a business's root page, otherwise StoreComponent) triggers the
  // fetch; everyone else on the same page (Cart/Orders/Payment/Products/
  // Discover) calls ensureCurrentBusiness too and gets the same request back
  // instead of firing a second identical call. A different business name
  // simply overwrites this slot — nothing accumulates.
  private currentBusinessName: string | null = null;
  private currentBusinessRequest$: Observable<any> | null = null;

  // Same single-slot idea for a Delivery business's children — the cart
  // badge count and the full cart/orders/payment pages all ask for the same
  // parent's children independently.
  private currentChildBusinessesParentName: string | null = null;
  private currentChildBusinessesRequest$: Observable<any[]> | null = null;

  constructor(private http: HttpClient) {}

  discoverBusinesses(data, businessName?: string) {
    const suffix = businessName ? `/${businessName}` : '';
    return this.http
      .post(`${environment.apiUrl}/business/discover${suffix}`, data)
      .pipe();
  }

  // Unique list of a Delivery business's child businesses, derived from its
  // (unfiltered) discover results — used to aggregate carts/orders across
  // all children on the parent's own cart/payment/orders pages.
  getChildBusinesses(parentBusinessName: string): Observable<any[]> {
    return this.discoverBusinesses({}, parentBusinessName).pipe(
      map((branches: any[]) => {
        const seen = new Map<number, any>();
        (branches || []).forEach((branch: any) => {
          if (branch.business && !seen.has(branch.business.id)) {
            seen.set(branch.business.id, branch.business);
          }
        });
        return Array.from(seen.values());
      }),
    );
  }

  // Resolves parentBusinessName's children, reusing the in-flight/resolved
  // request for that same parent instead of firing /business/discover again.
  ensureChildBusinesses(parentBusinessName: string): Observable<any[]> {
    if (
      this.currentChildBusinessesParentName === parentBusinessName &&
      this.currentChildBusinessesRequest$
    ) {
      return this.currentChildBusinessesRequest$;
    }

    this.currentChildBusinessesParentName = parentBusinessName;
    this.currentChildBusinessesRequest$ = this.getChildBusinesses(
      parentBusinessName,
    ).pipe(
      shareReplay(1),
      catchError((error) => {
        this.currentChildBusinessesParentName = null;
        this.currentChildBusinessesRequest$ = null;
        return throwError(() => error);
      }),
    );
    return this.currentChildBusinessesRequest$;
  }

  // Plain, uncached fetch — used directly for one-off queries (e.g.
  // ProductsComponent's own search/sort/pagination requests) that are never
  // "the current business" and shouldn't be reused across different queries.
  getStoreByBusinessName(business_name, data): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/business-find/${business_name}`,
      data,
    );
  }

  // Resolves businessName, reusing the in-flight/resolved request for that
  // same business instead of firing a second identical business-find call.
  // From its `type` and `parent_business_id` fields, callers can tell
  // whether it's a Delivery business (has child businesses), a standalone
  // Merchant (parent_business_id 0), or a child business of a Delivery
  // business (parent_business_id set) — see isDeliveryBusiness/
  // isChildBusiness. When it's a child business, the response also embeds
  // `parentBusiness` for branding (see BusinessController.getBusinessByName).
  ensureCurrentBusiness(businessName: string, data: any = {}): Observable<any> {
    if (
      this.currentBusinessName === businessName &&
      this.currentBusinessRequest$
    ) {
      return this.currentBusinessRequest$;
    }

    this.currentBusinessName = businessName;
    this.currentBusinessRequest$ = this.getStoreByBusinessName(
      businessName,
      data,
    ).pipe(
      shareReplay(1),
      catchError((error) => {
        this.currentBusinessName = null;
        this.currentBusinessRequest$ = null;
        return throwError(() => error);
      }),
    );
    return this.currentBusinessRequest$;
  }

  isDeliveryBusiness(business: any): boolean {
    return business?.type === AccountTypes.Delivery;
  }

  // A business is a "child" business when it belongs to a Delivery business
  // (parent_business_id set) — as opposed to a standalone Merchant, whose
  // parent_business_id is 0.
  isChildBusiness(business: any): boolean {
    return !!business && Number(business.parent_business_id) > 0;
  }

  // Applies a business's theme colors as CSS custom properties (see
  // tailwind.config.js's rgb(var(...)) colors) so tw-bg-primary etc. pick
  // them up everywhere, with no per-component CSS. Shared by StoreComponent
  // (storefront pages) and DiscoverComponent (a Delivery business's own
  // discover root, nested inside StoreComponent's shell — see
  // store-routing.module.ts).
  applyTheme(businessName: string, source: any): void {
    localStorage.setItem(businessName + '-primary-color', source.primary_color);
    localStorage.setItem(businessName + '-secondary-color', source.secondary_color);
    // Kept as plain hex for consumers that need a CSS color value directly
    // (e.g. the shipping map marker's SVG fill).
    document.documentElement.style.setProperty(
      '--primary-color',
      source.primary_color,
    );
    document.documentElement.style.setProperty(
      '--secondary-color',
      source.secondary_color,
    );
    document.documentElement.style.setProperty(
      '--primary-color-rgb',
      this.hexToRgbChannels(source.primary_color),
    );
    document.documentElement.style.setProperty(
      '--secondary-color-rgb',
      this.hexToRgbChannels(source.secondary_color),
    );
  }

  // Clears the theme colors so they don't leak into pages outside the
  // storefront/discover context (dashboard, home, login, ...).
  clearTheme(): void {
    document.documentElement.style.removeProperty('--primary-color');
    document.documentElement.style.removeProperty('--secondary-color');
    document.documentElement.style.removeProperty('--primary-color-rgb');
    document.documentElement.style.removeProperty('--secondary-color-rgb');
  }

  private hexToRgbChannels(hex: string): string {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!match) {
      return '';
    }
    const [, r, g, b] = match;
    return `${parseInt(r, 16)} ${parseInt(g, 16)} ${parseInt(b, 16)}`;
  }
}
